import { depthFrameMatches } from "../player/depthFrame";
import { useBitmapPreview } from "../player/useBitmapPreview";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefCallback } from "react";

/**
 * The depth overlay, as a video.
 *
 * Per-frame PNGs could only ever be as smooth as the file list, and writing 600
 * of them for a 20 s clip cost more than the stereo matching did. The backend
 * now emits one grayscale video whose luma is **linear in disparity** with 0
 * reserved for "no depth here", and this component turns it into colour.
 *
 * Doing the colour mapping here rather than baking it in is what makes the
 * display adjustable — opacity, range and palette change without re-running
 * anything — and it is the only way to get transparency, since a lossy alpha
 * channel does not survive a WebM round trip.
 *
 * The video element itself is registered with the player, so it seeks and plays
 * against the same master clock as the two eyes.
 */

const INVALID_BELOW = 3; // lossy compression can drag a 0 up a step or two

export function DepthLayer({
  url,
  targetTime,
  paused = false,
  frameUrl,
  referenceVideo,
  referenceTime,
  referencePreview,
  nearbyFrames = [],
  relative = false,
  register,
  onLoadedMetadata,
  opacity,
  nearM,
  farM,
  displayNearM,
  displayFarM,
  onHoverDepth,
}: {
  url: string;
  targetTime: number;
  paused?: boolean;
  frameUrl?: string;
  referenceVideo?: () => HTMLVideoElement | null;
  referenceTime?: number;
  referencePreview?: () => boolean;
  nearbyFrames?: string[];
  relative?: boolean;
  register: RefCallback<HTMLVideoElement>;
  onLoadedMetadata: () => void;
  opacity: number;
  nearM: number;
  farM: number;
  displayNearM?: number;
  displayFarM?: number;
  onHoverDepth?: (metres: number | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scratchRef = useRef<HTMLCanvasElement | null>(null);
  const lutRef = useRef<Uint32Array>(new Uint32Array(256));
  const lastRef = useRef<number>(-1);
  const targetRef = useRef(targetTime);
  targetRef.current = targetTime;
  const image = useBitmapPreview(frameUrl, paused, nearbyFrames);
  const previewRef = useRef({ paused, frameUrl, referenceVideo, referenceTime, referencePreview, ...image });
  previewRef.current = { paused, frameUrl, referenceVideo, referenceTime, referencePreview, ...image };
  const [status, setStatus] = useState("loading");
  const statusRef = useRef(status);
  const reportStatus = useCallback((next: string) => {
    if (statusRef.current !== next) { statusRef.current = next; setStatus(next); }
  }, []);
  const validRef = useRef(false);
  const attach = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element; register(element);
  }, [register]);
  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    validRef.current = false;
    lastRef.current = -1;
  }, []);
  useLayoutEffect(() => {
    const video = videoRef.current;
    if ((paused && frameUrl) || !video || video.seeking || !depthFrameMatches(video.currentTime, targetTime, video.duration, video.readyState)) clear();
  }, [targetTime, url, paused, frameUrl, clear]);

  useLayoutEffect(() => { lastRef.current = -1; }, [image.bitmap]);

  /* The palette, rebuilt only when the display range or opacity changes. */
  useEffect(() => {
    if(relative) {
      const lut = new Uint32Array(256);
      const alpha = Math.round(opacity*255)<<24;
      for(let i=3;i<256;i++){const [r,g,b]=ramp(Math.max(0,(i-4)/251));lut[i]=alpha|(b<<16)|(g<<8)|r;}
      lutRef.current=lut;
    } else lutRef.current = buildLut(opacity, nearM, farM, displayNearM ?? nearM, displayFarM ?? farM);
    lastRef.current = -1; // force a repaint with the new palette
  }, [opacity, nearM, farM, displayNearM, displayFarM, relative]);

  useEffect(() => {
    let raf = 0;
    let cancelled = false;
    let frameCallback = 0;

    const paint = (decodedTime?: number) => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;
      const preview = previewRef.current;
      const reference = preview.referenceVideo?.();
      const referenceReady = preview.paused && preview.referencePreview ? preview.referencePreview() : !reference || (!reference.seeking && depthFrameMatches(reference.currentTime, preview.referenceTime ?? targetRef.current, reference.duration, reference.readyState));
      if (!referenceReady) {
        clear(); reportStatus("loading"); return;
      }
      const stillMode = preview.paused && !!preview.frameUrl;
      const still = stillMode ? preview.bitmap : undefined;
      if (stillMode && !still) {
        clear(); reportStatus(preview.failed ? "error" : "loading"); return;
      }
      if (!still && (video.seeking || !depthFrameMatches(decodedTime ?? video.currentTime, targetRef.current, video.duration, video.readyState))) { clear(); reportStatus("loading"); return; }
      const w = still?.width ?? video.videoWidth;
      const h = still?.height ?? video.videoHeight;
      if (!w || !h) return;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      if (!scratchRef.current) scratchRef.current = document.createElement("canvas");
      const scratch = scratchRef.current;
      if (scratch.width !== w || scratch.height !== h) {
        scratch.width = w;
        scratch.height = h;
      }
      const sctx = scratch.getContext("2d", { willReadFrequently: true });
      const ctx = canvas.getContext("2d");
      if (!sctx || !ctx) return;

      sctx.drawImage(still ?? video, 0, 0, w, h);
      const image = sctx.getImageData(0, 0, w, h);
      const pixels = new Uint32Array(image.data.buffer);
      const lut = lutRef.current;
      // The source is grayscale, so the red channel carries the value. One
      // typed-array pass with a 256-entry lookup keeps this well under a
      // millisecond at 640x480.
      for (let i = 0; i < pixels.length; i++) {
        pixels[i] = lut[pixels[i] & 0xff];
      }
      ctx.putImageData(image, 0, 0);
      validRef.current = true;
      reportStatus("ready");
    };

    /* `requestVideoFrameCallback` repaints exactly once per decoded frame;
       without it, fall back to the animation frame. */
    const video = videoRef.current;
    const supportsFrameCallback =
      video != null && typeof (video as any).requestVideoFrameCallback === "function";

    if (supportsFrameCallback) {
      const step = (_now: number, metadata: VideoFrameCallbackMetadata) => {
        if (cancelled) return;
        paint(metadata.mediaTime);
        lastRef.current = video!.currentTime;
        frameCallback = video!.requestVideoFrameCallback(step);
      };
      frameCallback = video!.requestVideoFrameCallback(step);
    }
    // Paused/hidden videos may not deliver a compositor callback after a seek.
    // Media events and this fallback also clear stale pixels during buffering.
    const loop = () => {
      if (cancelled) return;
      const current = videoRef.current?.currentTime ?? -1;
      // With a compositor callback, advancing currentTime alone does not mean
      // there is a new decoded frame. Avoid colour-mapping it again at screen Hz.
      const needsFallback = !supportsFrameCallback || previewRef.current.paused;
      if (!validRef.current || lastRef.current < 0 || (needsFallback && current !== lastRef.current) || (!previewRef.current.paused && video?.seeking)) {
        paint(); lastRef.current = current;
      }
      raf = requestAnimationFrame(loop);
    };
    const repaint = () => paint();
    for (const event of ["seeking", "emptied", "error", "waiting"]) video?.addEventListener(event, clear);
    for (const event of ["seeked", "loadeddata", "canplay"]) video?.addEventListener(event, repaint);
    clear(); paint(); raf = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (frameCallback) video?.cancelVideoFrameCallback(frameCallback);
      for (const event of ["seeking", "emptied", "error", "waiting"]) video?.removeEventListener(event, clear);
      for (const event of ["seeked", "loadeddata", "canplay"]) video?.removeEventListener(event, repaint);
      clear();
    };
  }, [url, opacity, nearM, farM, displayNearM, displayFarM, relative]);

  const readDepth = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onHoverDepth) return;
    const canvas = canvasRef.current;
    const scratch = scratchRef.current;
    if (!canvas || !scratch || !validRef.current) { onHoverDepth(null); return; }
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor(((event.clientX - rect.left) / rect.width) * scratch.width);
    const y = Math.floor(((event.clientY - rect.top) / rect.height) * scratch.height);
    const sctx = scratch.getContext("2d", { willReadFrequently: true });
    if (!sctx) return;
    const value = sctx.getImageData(Math.max(0, x), Math.max(0, y), 1, 1).data[0];
    onHoverDepth(value < INVALID_BELOW ? null : decodeDepth(value, nearM, farM));
  };

  return (
    <>
      <video
        ref={attach}
        src={url}
        muted
        playsInline
        preload={paused && frameUrl ? "metadata" : "auto"}
        onLoadedMetadata={onLoadedMetadata}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
      <canvas
        ref={canvasRef}
        data-depth-state={status}
        data-depth-source={paused && frameUrl ? "frame" : "video"}
        onMouseMove={readDepth}
        onMouseLeave={() => onHoverDepth?.(null)}
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          pointerEvents: onHoverDepth ? "auto" : "none",
        }}
      />
      {paused && frameUrl && status !== "ready" && <span className="depth-preview-status" role="status">{status === "error" ? "Depth preview unavailable" : "Loading depth…"}</span>}
    </>
  );
}

/** value -> metres, inverting the backend's disparity-linear encoding. */
export function decodeDepth(value: number, nearM: number, farM: number): number {
  const invNear = 1 / Math.max(nearM, 1e-6);
  const invFar = 1 / Math.max(farM, 1e-6);
  const t = (value - 1) / 254;
  return 1 / (invFar + Math.min(1, Math.max(0, t)) * (invNear - invFar));
}

/**
 * 256 packed RGBA entries: warm near, cool far, transparent where invalid.
 *
 * The palette is spread over the *display* range rather than the encoded one,
 * so narrowing the range brings out detail at a chosen distance without
 * touching the data.
 */
function buildLut(
  opacity: number, nearM: number, farM: number, displayNear: number, displayFar: number,
): Uint32Array {
  const lut = new Uint32Array(256);
  const alpha = Math.round(Math.min(1, Math.max(0, opacity)) * 255) << 24;
  for (let value = 0; value < 256; value++) {
    if (value < INVALID_BELOW) {
      lut[value] = 0; // fully transparent: no measurement here
      continue;
    }
    const metres = decodeDepth(value, nearM, farM);
    const t = Math.min(1, Math.max(0, (metres - displayNear) / Math.max(1e-6, displayFar - displayNear)));
    const [r, g, b] = ramp(1 - t); // 1 = near
    lut[value] = alpha | (b << 16) | (g << 8) | r;
  }
  return lut;
}

/** A perceptually ordered warm-to-cool ramp; 1 is near, 0 is far. */
function ramp(t: number): [number, number, number] {
  const stops: [number, number, number, number][] = [
    [0.0, 40, 30, 110],   // far: deep indigo
    [0.25, 30, 110, 190], // blue
    [0.5, 40, 180, 150],  // teal
    [0.75, 240, 190, 60], // amber
    [1.0, 240, 70, 50],   // near: red
  ];
  const x = Math.min(1, Math.max(0, t));
  for (let i = 1; i < stops.length; i++) {
    if (x <= stops[i][0]) {
      const [x0, r0, g0, b0] = stops[i - 1];
      const [x1, r1, g1, b1] = stops[i];
      const k = (x - x0) / (x1 - x0);
      return [
        Math.round(r0 + (r1 - r0) * k),
        Math.round(g0 + (g1 - g0) * k),
        Math.round(b0 + (b1 - b0) * k),
      ];
    }
  }
  return [240, 70, 50];
}
